/**
 * 资产清单——资源路径的唯一真实来源。
 * Phaser 的 preload 管线和 UI 都从此处读取，而非硬编码文件路径。
 */
export interface AssetManifest {
  towers: Record<string, string>;
  enemies: Record<string, string>;
  effects: Record<string, string>;
  audio: Record<string, string>;
}

/** 首批按内容 ID 管理的可替换角色外观。 */
export const assetManifest: AssetManifest = {
  towers: {
    arrow: "assets/skins/towers/arrow.png",
    cannon: "assets/skins/towers/cannon.png",
    ice_tower: "assets/skins/towers/ice_tower.png",
    thunder_tower: "assets/skins/towers/thunder_tower.png",
    magic_tower: "assets/skins/towers/magic_tower.png",
  },
  enemies: {
    grunt: "assets/skins/enemies/grunt.png",
    runner: "assets/skins/enemies/runner.png",
    bruiser: "assets/skins/enemies/bruiser.png",
    tank: "assets/skins/enemies/tank.png",
    swarm: "assets/skins/enemies/swarm.png",
    spider: "assets/skins/enemies/spider.png",
    frost_giant: "assets/skins/enemies/frost_giant.png",
    ice_elemental: "assets/skins/enemies/ice_elemental.png",
    frost_lord: "assets/skins/enemies/frost_lord.png",
    warlord: "assets/skins/enemies/warlord.png",
    storm_dragon: "assets/skins/enemies/storm_dragon.png",
    shadow_knight: "assets/skins/enemies/shadow_knight.png",
  },
  effects: {},
  audio: {},
};

/**
 * 通过资产管线加载清单。当条目的值是相对路径时，
 * 相对于 `baseUrl` 解析。
 *
 * 返回 `contentId → 已加载资源 URL` 的映射，供调用方
 * 传递给 `this.load.*` 调用（或保留给未来的 HTTP 获取层）。
 */
export function resolveAssetUrls(manifest: AssetManifest, baseUrl: string): Record<string, string> {
  const out: Record<string, string> = {};
  const root = baseUrl || (typeof document === "undefined" ? "" : document.baseURI);
  const resolve = (path: string): string => {
    if (!root) return path;
    try {
      return new URL(path, root).toString();
    } catch {
      return root + path;
    }
  };
  for (const [id, relPath] of Object.entries(manifest.towers))
    out[`tower.${id}`] = resolve(relPath);
  for (const [id, relPath] of Object.entries(manifest.enemies))
    out[`enemy.${id}`] = resolve(relPath);
  for (const [id, relPath] of Object.entries(manifest.effects))
    out[`effect.${id}`] = resolve(relPath);
  for (const [id, relPath] of Object.entries(manifest.audio)) out[`audio.${id}`] = resolve(relPath);
  return out;
}

/** 基于资源类别和内容 ID 生成稳定的 Phaser 纹理键。 */
export function assetTextureKey(
  category: "tower" | "enemy" | "effect" | "audio",
  id: string,
): string {
  return `${category}.${id}`;
}
